import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withTranslator } from 'tradera-lang/translate';

import PAGE_TYPES from 'tradera-constants/page-types';
import ItemCard from 'tradera-components/item-card/item-card';
import ItemListCard from 'tradera-components/item-card/item-list-card';
import { lazyLoadSettings } from 'tradera-components/item-card/config';
import ZeroResults from './zero-results';
import { DesktopAd, TabletAd, MobileAd } from './ads/search-results-ads';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';
import ItemsWithStitchedAds from './items-with-stiched-ads';

import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';

import GtmService from 'static/script/app/ui/google-tagmanager-service';

import 'tradera-components/item-card/layout.scss';
import initData from 'packages/init-data';
import { registerLiveUpdates } from 'tradera-utils/register-live-updates';
import './search-results.scss';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';

const SearchResults = ({
    fallbackItemsById,
    featureSwitches,
    handleOpenItemSettings,
    handleItemAttributesForFiltering,
    isShopOwnedByCurrentMember,
    itemCache,
    itemLayout,
    itemsById,
    itemsOnDisplayById,
    openQuickView,
    pageIndex,
    pageType,
    preferredCurrency,
    queryParams,
    t,
    toggleWishlist,
    updateItem,
    fallbackUrl,
    siteLayout,
    noItemsFallback,
    isLoaded
}) => {
    const items = itemsById.map(id => itemCache[id]);
    const fallbackItems = fallbackItemsById.map(id => itemCache[id]);
    const itemsOnDisplay = itemsOnDisplayById.map(id => itemCache[id]);

    const { q: searchTerm } = queryParams;

    const adsEnabled = featureSwitches['google-ads'] === true;
    const quickViewEnabled = featureSwitches['quick-view'];

    const showItemsOnDisplay =
        (itemsOnDisplay && itemsOnDisplay.length > 0) ||
        isShopOwnedByCurrentMember;

    const isLastChanceLiveUppdatesEnabled = useIsFeatureEnabled(
        'last-chance-live-updates'
    );
    useEffect(() => {
        if (
            pageType === PAGE_TYPES.CUSTOM_RESULT_PAGE &&
            isLastChanceLiveUppdatesEnabled
        ) {
            return registerLiveUpdates({
                url: initData.webLiveUrl,
                onConnect: socket => {
                    for (let { itemId } of items) {
                        socket.emit('join', `item:${itemId}`);
                    }
                },
                onClientEvent: data => {
                    switch (data.Type) {
                        case 'ItemSold':
                        case 'ItemUnSold':
                            break;
                        case 'AuctionLeadingBidUpdated':
                        case 'IAuctionLeadingBidUpdated':
                            updateItem({
                                itemId: data.Event.AuctionId,
                                item: {
                                    price: data.Event.BidAmount,
                                    totalBids: data.Event.NumberOfBids
                                }
                            });
                            break;
                    }
                }
            });
        }
    }, [pageType, items, updateItem, isLastChanceLiveUppdatesEnabled]);

    const handleQuickViewClick = useCallback(
        itemId => {
            openQuickView(itemId, itemsById);
        },
        [itemsById, openQuickView]
    );
    const handleAddToWishlist = useCallback(
        itemId => {
            GtmService.trackAction('Wishlist Item', `Add From ${pageType}`);
            toggleWishlist(itemId);
        },
        [pageType, toggleWishlist]
    );
    const handleRemoveFromWishlist = useCallback(
        itemId => {
            GtmService.trackAction('Wishlist Item', `Remove From ${pageType}`);
            toggleWishlist(itemId);
        },
        [pageType, toggleWishlist]
    );
    const handleItemAttributesForFilterClick = attribute => {
        const { name, values } = attribute;
        GtmService.trackAction('Search', 'ItemCard filter', `${name}`);
        handleItemAttributesForFiltering(name, values);
    };

    return (
        <div
            data-search-results-items=""
            className={`item-card-layout ${itemLayout} px-1 pr-0 pb-4 ${
                items && items.length > 20 ? 'search-results' : 'few-results'
            }`}>
            {showItemsOnDisplay && (
                <section>
                    <div className="col-12 d-flex mb-2">
                        <div className="w-100 h-100 m-auto border-bottom" />
                        <h3 className="text-center text-nowrap text-styled mb-0 px-5">
                            Utvalda objekt
                        </h3>
                        <div className="w-100 h-100 m-auto border-bottom" />
                    </div>
                    <div className="row search-results--on-display">
                        <ItemsOnDisplay
                            isShopOwnedByCurrentMember={
                                isShopOwnedByCurrentMember
                            }
                            itemsOnDisplay={itemsOnDisplay}
                            itemLayout={itemLayout}
                            quickViewEnabled={quickViewEnabled}
                            handleQuickViewClick={handleQuickViewClick}
                            handleRemoveFromWishlist={handleRemoveFromWishlist}
                            handleAddToWishlist={handleAddToWishlist}
                            handleOpenItemSettings={handleOpenItemSettings}
                            handleItemAttributesForFilterClick={
                                handleItemAttributesForFilterClick
                            }
                            preferredCurrency={preferredCurrency}
                        />
                    </div>
                    <div className="col-12 border-bottom mb-4" />
                </section>
            )}

            {items?.length > 0 || fallbackItems?.length > 0 ? (
                <>
                    <section className="row mb-4">
                        <ItemsWithStitchedAds
                            adsEnabled={adsEnabled && isLoaded}
                            handleAddToWishlist={handleAddToWishlist}
                            handleQuickViewClick={handleQuickViewClick}
                            handleRemoveFromWishlist={handleRemoveFromWishlist}
                            handleOpenItemSettings={handleOpenItemSettings}
                            handleItemAttributesForFilterClick={
                                handleItemAttributesForFilterClick
                            }
                            itemLayout={itemLayout}
                            items={items}
                            pageIndex={pageIndex}
                            preferredCurrency={preferredCurrency}
                            quickViewEnabled={quickViewEnabled}
                            siteLayout={siteLayout}
                            skipTopAd={
                                pageType === PAGE_TYPES.CUSTOM_RESULT_PAGE
                            }
                            isShopOwnedByCurrentMember={
                                isShopOwnedByCurrentMember
                            }
                        />
                    </section>
                    {fallbackItems?.length > 0 && (
                        <section className="row mb-4">
                            <h3 className="pb-3 col-12">
                                {t('searchResults_fallback_label', {
                                    searchTerm: searchTerm || ''
                                })}
                            </h3>
                            <FallbackItems
                                fallbackItems={fallbackItems}
                                handleAddToWishlist={handleAddToWishlist}
                                handleQuickViewClick={handleQuickViewClick}
                                handleRemoveFromWishlist={
                                    handleRemoveFromWishlist
                                }
                                handleItemAttributesForFilterClick={
                                    handleItemAttributesForFilterClick
                                }
                                itemLayout={itemLayout}
                                preferredCurrency={preferredCurrency}
                                quickViewEnabled={quickViewEnabled}
                            />
                            {fallbackUrl && (
                                <div className="col-12">
                                    <Link
                                        className="btn btn-outline-dark"
                                        to={fallbackUrl}>
                                        {t('searchResults_fallback_link')}
                                    </Link>
                                </div>
                            )}
                        </section>
                    )}
                </>
            ) : (
                <div>
                    {noItemsFallback ? (
                        noItemsFallback
                    ) : (
                        <ZeroResults searchTerm={searchTerm} />
                    )}
                    {isLoaded && (
                        <>
                            <DesktopAd
                                adContainerClass="mt-3"
                                index={99}
                                autoscale={true}
                            />
                            <MobileAd index={99} autoscale={true} />
                            <TabletAd index={99} autoscale={true} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

SearchResults.propTypes = {
    fallbackItemsById: PropTypes.array,
    isShopOwnedByCurrentMember: PropTypes.bool,
    itemCache: PropTypes.object.isRequired,
    itemLayout: PropTypes.string.isRequired,
    itemsById: PropTypes.array.isRequired,
    itemsOnDisplayById: PropTypes.array.isRequired,
    openQuickView: PropTypes.func.isRequired,
    pageIndex: PropTypes.number,
    pageType: PropTypes.string,
    preferredCurrency: PropTypes.object,
    queryParams: PropTypes.object,
    toggleWishlist: PropTypes.func.isRequired,
    updateItem: PropTypes.func.isRequired,
    fallbackUrl: PropTypes.string,
    noItemsFallback: PropTypes.node
};

SearchResults.defaultProps = {
    adsEnabled: false,
    noItemsFallback: null
};

const ItemsOnDisplay = ({
    isShopOwnedByCurrentMember,
    itemsOnDisplay,
    itemLayout,
    quickViewEnabled,
    handleQuickViewClick,
    handleRemoveFromWishlist,
    handleAddToWishlist,
    handleOpenItemSettings,
    handleItemAttributesForFilterClick,
    preferredCurrency
}) => {
    const itemsOnDisplayCards = [];

    const ItemCardComponent =
        itemLayout === 'layout-list' ? ItemListCard : ItemCard;
    itemsOnDisplay.map(item => {
        itemsOnDisplayCards.push(
            <div
                key={item.itemId}
                className={`item-card-container ${
                    itemLayout === 'layout-list'
                        ? 'pb-2 mb-2 border-bottom'
                        : ''
                }`}>
                <ItemCardComponent
                    className={
                        itemLayout === 'layout-list' ? '' : 'mr-md-1 mb-1'
                    }
                    item={item}
                    lazy={lazyLoadSettings}
                    onAddToWishlist={handleAddToWishlist}
                    onAttributeButtonClick={handleItemAttributesForFilterClick}
                    onQuickView={quickViewEnabled && handleQuickViewClick}
                    onRemoveFromWishlist={handleRemoveFromWishlist}
                    onSettings={handleOpenItemSettings}
                    preferredCurrency={preferredCurrency}
                    ready={!!item.itemUrl}
                    isShopOwnedByCurrentMember={isShopOwnedByCurrentMember}
                />
            </div>
        );
    });
    if (isShopOwnedByCurrentMember && itemsOnDisplayCards.length < 4) {
        for (
            let placeHolderCards = itemsOnDisplayCards.length;
            placeHolderCards < 4;
            placeHolderCards++
        ) {
            itemsOnDisplayCards.push(
                <div
                    key={placeHolderCards}
                    className={`item-card-container ${
                        itemLayout === 'layout-list'
                            ? 'pb-2 mb-2 border-bottom'
                            : ''
                    }`}>
                    <div className="on-display-placeholder d-flex flex-column justify-content-center align-items-center h-100 bg-gray-100 border py-md-5 py-3 mx-2">
                        <InfoIcon className="icon icon-xl text-gray-400 mb-3" />
                        <span className="text-gray-600">Ledig plats</span>
                        <span className="text-gray-600">
                            {placeHolderCards + 1} av 4
                        </span>
                    </div>
                </div>
            );
        }
    }

    return itemsOnDisplayCards;
};

const FallbackItems = ({
    fallbackItems,
    itemLayout,
    quickViewEnabled,
    handleQuickViewClick,
    handleRemoveFromWishlist,
    handleAddToWishlist,
    handleItemAttributesForFilterClick,
    preferredCurrency
}) => {
    const fallbackItemsCards = [];

    const ItemCardComponent =
        itemLayout === 'layout-list' ? ItemListCard : ItemCard;

    fallbackItems.map(item => {
        fallbackItemsCards.push(
            <div
                key={item.itemId}
                className={`item-card-container ${
                    itemLayout === 'layout-list'
                        ? 'pb-2 mb-2 border-bottom'
                        : ''
                }`}>
                <ItemCardComponent
                    className={
                        itemLayout === 'layout-list' ? '' : 'mr-md-1 mb-1'
                    }
                    item={item}
                    lazy={lazyLoadSettings}
                    onQuickView={quickViewEnabled && handleQuickViewClick}
                    onRemoveFromWishlist={handleRemoveFromWishlist}
                    onAddToWishlist={handleAddToWishlist}
                    onAttributeButtonClick={handleItemAttributesForFilterClick}
                    preferredCurrency={preferredCurrency}
                    ready={!!item.itemUrl}
                />
            </div>
        );
    });

    return fallbackItemsCards;
};

const mapStateToProps = state => {
    return {
        fallbackItemsById: state.discover.fallbackItemsById,
        featureSwitches: state.environment.featureSwitches,
        isLoggedIn: state.member.isLoggedIn,
        itemCache: state.items.cache,
        itemLayout: state.ui.itemLayout,
        siteLayout: state.ui.siteLayout,
        itemsById: state.discover.itemsById,
        itemsOnDisplayById: state.discover.itemsOnDisplayById,
        pageIndex: state.discover.pagination
            ? state.discover.pagination.pageIndex
            : null,
        preferredCurrency: state.multiCurrency.preferredCurrency,
        queryParams: state.discover.queryParams,
        fallbackUrl: state.discover.fallbackUrl
    };
};

const mapDispatchToProps = dispatch => {
    return {
        openQuickView: (itemId, itemIds) =>
            dispatch(
                uiActions.openQuickView({
                    itemIds,
                    itemId,
                    openedFrom: 'Search Results'
                })
            ),
        toggleWishlist: itemId => dispatch(itemActions.wishlist(itemId)),
        updateItemLayout: itemLayout =>
            dispatch(uiActions.updateItemLayout(itemLayout)),
        updateItem: payload => dispatch(itemActions.updateItem(payload)),
        handleOpenItemSettings: item =>
            dispatch(uiActions.openItemSettings(item))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withTranslator(SearchResults));
